var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { forwardRef, useContext, useEffect, useState, useRef, } from "react";
import Text from "../text/Text";
import Box from "../box/Box";
import { ThemeContext } from "../../theme/ThemeProvider";
import { StyledErrorIcon, StyledIcon, StyledInput, StyledLabel, StyledWrapper } from "./Checkbox.style";
import { StyledHiddenScreenReaderOnly } from "../../utils/StyleUtils";
import useUniqueId from "../../hooks/useUniqueId";
import { apolloSpaceInlineHalfX, apolloSpaceInsetHalfX } from "@nexthink/apollo-tokens";
import useAutoFocus from "../../hooks/useAutoFocus";
import InfoIconTooltip from "../with-tooltip/InfoIconTooltip";
import Flex from "../box/Flex";
const prefixId = "nx-checkbox-";
const Checkbox = forwardRef(function Checkbox(_a, parentRef) {
    var { id, "data-insights": insightsAttr, name, disabled, autoFocus: initialAutoFocus, checked, indeterminate, display, error, onChange, onFocus, onBlur, label, infoTooltip, tabIndex, 
    // tooltip target props
    "aria-describedby": ariaDescribedBy, "aria-hidden": ariaHidden, onKeyDown, onMouseEnter, onMouseLeave, onPointerEnter, onPointerLeave, "data-portal-target": dataPortalTarget } = _a, rest = __rest(_a, ["id", "data-insights", "name", "disabled", "autoFocus", "checked", "indeterminate", "display", "error", "onChange", "onFocus", "onBlur", "label", "infoTooltip", "tabIndex", "aria-describedby", "aria-hidden", "onKeyDown", "onMouseEnter", "onMouseLeave", "onPointerEnter", "onPointerLeave", "data-portal-target"]);
    const autoFocus = initialAutoFocus && !disabled;
    const [focused, setFocused] = useState(autoFocus);
    const theme = useContext(ThemeContext);
    id = useUniqueId(prefixId, id);
    const inputRef = useRef(null);
    useAutoFocus(inputRef, autoFocus);
    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.indeterminate = Boolean(indeterminate);
        }
    }, [indeterminate]);
    const handleOnFocus = (e) => {
        setFocused(true);
        if (onFocus) {
            onFocus(e);
        }
    };
    const handleOnBlur = (e) => {
        setFocused(false);
        if (onBlur) {
            onBlur(e);
        }
    };
    const errorId = `${id}-error`;
    const hasError = error && !disabled;
    const hasErrorMessage = typeof error === "string";
    const renderError = (errorMessage) => (React.createElement(Box, Object.assign({ id: errorId, "data-insights": insightsAttr && `${insightsAttr}-error`, as: "span", display: "inline-flex" }, (errorMessage && { "aria-live": "polite" })),
        React.createElement(Box, { pl: apolloSpaceInlineHalfX, title: errorMessage, as: "span", display: "inline-flex", "aria-hidden": "true" },
            React.createElement(StyledErrorIcon, null)),
        errorMessage && React.createElement(StyledHiddenScreenReaderOnly, null, errorMessage)));
    const wrapperProps = {
        "aria-describedby": ariaDescribedBy,
        "aria-hidden": ariaHidden,
        onKeyDown,
        onMouseEnter,
        onMouseLeave,
        onPointerEnter,
        onPointerLeave,
        "data-portal-target": dataPortalTarget,
    };
    return (React.createElement(StyledWrapper, Object.assign({ display: display, ref: parentRef }, wrapperProps),
        React.createElement(StyledLabel, { htmlFor: id, checked: checked, disabled: disabled, error: error, focused: focused },
            React.createElement(StyledIcon, { disabled: disabled, checked: checked, indeterminate: indeterminate, focused: focused }),
            React.createElement(StyledInput, Object.assign({ ref: inputRef, type: "checkbox", id: id, "data-insights": insightsAttr, name: name, disabled: disabled, checked: checked, onFocus: handleOnFocus, onBlur: handleOnBlur }, (!disabled && { onChange }), (hasError && Object.assign({ "aria-invalid": true }, (hasErrorMessage && { "aria-errormessage": errorId }))), rest)),
            label && (React.createElement(Text, { color: disabled ? theme.colors.apolloColorTextDisabled : theme.colors.apolloColorTextPrimary }, label)),
            hasError && renderError(hasErrorMessage ? error : undefined)),
        infoTooltip && (React.createElement(Flex, { ml: apolloSpaceInsetHalfX, alignItems: "center" },
            React.createElement(InfoIconTooltip, null, infoTooltip)))));
});
export default Checkbox;
//# sourceMappingURL=Checkbox.js.map